import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { userIdentityShape, ugcSimilarTitlesShape, bibShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import { ExpandableList } from 'app/components/shared/Expandable';
import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import SimilarTitleDisplay from '../../../shared/UGC/SimilarTitleDisplay';
import SimilarTitlesUGCEditor from '../../../shared/UGC/UGCEditors/SimilarTitlesUGCEditor';
import DisplayHeading from '../../../shared/UGC/DisplayHeading';
import AddButton from '../../../shared/UGC/AddButton';
import { AddTitleOverlay } from '../../../shared/AddTitle';

import './SimilarTitlesInfo.scss';

function renderExpandButtonLabel(expanded, bib) {
  const title = bib.getIn(['briefInfo', 'title']);

  if (expanded) {
    return (
      <span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_show_less_similar_titles" values={{ title }} />
        </ScreenReaderMessage>
        <span aria-hidden>
          <FormattedMessage id="button_show_less" />
        </span>
      </span>
    );
  }

  return (
    <span>
      <ScreenReaderMessage>
        <FormattedMessage id="ada_show_more_similar_titles" values={{ title }} />
      </ScreenReaderMessage>
      <span aria-hidden>
        <FormattedMessage id="button_show_more" />
      </span>
    </span>
  );
}

export default class ShelfListItemSimilarTitlesInfo extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      showEditor: false
    };
  }

  closeEditor() {
    this.setState({ showEditor: false });
  }

  openEditor() {
    this.setState({ showEditor: true });
  }

  renderEditor() {
    return (
      this.state.showEditor && (
        <SimilarTitlesUGCEditor
          bib={this.props.bib}
          shelfItem={this.props.item}
          similarTitleBibs={this.props.similarTitleBibs}
          similarTitlesTransactions={this.props.similarTitlesTransactions}
          shelfTransactions={this.props.shelfTransactions}
          similarTitlesActions={this.props.similarTitlesActions}
          shelfActions={this.props.shelfActions}
          handleClose={this.closeEditor}
        />
      )
    );
  }

  renderSimilarTitleDisplay(similarTitle) {
    const { similarTitleBibs, similarTitlesActions, similarTitlesTransactions, owner } = this.props;
    const similarTitleBib = similarTitleBibs.get(similarTitle.get('similarMetadataId'));
    if (!similarTitleBib) {
      return null;
    }

    return (
      <SimilarTitleDisplay
        similarTitleBib={similarTitleBib}
        similarTitle={similarTitle}
        similarTitlesActions={similarTitlesActions}
        similarTitlesTransactions={similarTitlesTransactions}
        owner={owner}
      />
    );
  }

  render() {
    const { bib, ownerSimilarTitlesForBib, owner } = this.props;

    if (ownerSimilarTitlesForBib && ownerSimilarTitlesForBib.size > 0) {
      const count = ownerSimilarTitlesForBib.size;
      return (
        <div className="cp-shelf-list-item-similar-titles-info">
          <DisplayHeading>
            <FormattedMessage id="ugc_similar_titles_display_label" values={{ count }} />
          </DisplayHeading>
          <AddButton
            owner={owner}
            handleClick={this.openEditor}
            screenReaderMessage={
              <FormattedMessage id="ada_add_similar_title" values={{ title: bib.getIn(['briefInfo', 'title']) }} />
            }
          />
          <ExpandableList
            collapseCount={2}
            itemClassName="similar-titles-bib"
            list={ownerSimilarTitlesForBib.toList()}
            getItemKey={similarTitleBib => similarTitleBib.get('id')}
            renderItem={this.renderSimilarTitleDisplay}
            renderExpandButtonLabel={expanded => renderExpandButtonLabel(expanded, bib)}
          />

          <AddTitleOverlay open={!!this.state.showEditor} handleClose={this.closeEditor}>
            {this.renderEditor()}
          </AddTitleOverlay>
        </div>
      );
    }

    return null;
  }
}

ShelfListItemSimilarTitlesInfo.propTypes = {
  bib: bibShape.isRequired,
  ownerSimilarTitlesForBib: ImmutablePropTypes.listOf(ugcSimilarTitlesShape),
  similarTitleBibs: ImmutablePropTypes.map,
  item: shelfItemShape.isRequired,
  owner: userIdentityShape.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired
};
